/**
 * Прочие расходы на экране «Финансы» (docs/08-screens.md): трата без сервиса
 * и без кошелька — домен, реклама, рублёвый сервис. Входят в траты месяца
 * строкой «Прочие» (docs/04-finance.md). То же вносится командой бота
 * /expense 1500 комментарий.
 */
import { useEffect, useState } from 'react';
import { Button, Card, Dialog, Icon, Input } from '@/ds';
import { ApiError } from '@/api/client';
import { useExpenses, useOperation, type ExpenseRow } from '@/api/hooks';
import { DateField, MoneyField } from '@/components/fields';
import { Money, Provenance } from '@/components/money';
import { QueryState } from '@/components/QueryState';
import { formatDate, formatMoney, plural } from '@/lib/format';
import { parseMinor, todayInputValue } from '@/lib/money';
import { useControlSize } from '@/lib/useControlSize';
import { useIsMobile } from '@/lib/useMediaQuery';
import { recordSourceLabel } from '../wallets/walletView';

function AddExpenseDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const size = useControlSize();
  const create = useOperation<{ date: string; amount: number; comment: string }>('expense.create');

  const [date, setDate] = useState(todayInputValue());
  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDate(todayInputValue());
    setAmount('');
    setComment('');
    setLocalError(null);
    create.reset();
    // Сброс один раз при открытии.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const serverError = create.error instanceof ApiError ? create.error.message : null;

  function submit() {
    setLocalError(null);
    const minor = parseMinor(amount);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setLocalError('Укажите дату расхода');
      return;
    }
    if (minor === null || minor <= 0) {
      setLocalError('Укажите сумму в рублях');
      return;
    }
    if (comment.trim().length === 0) {
      setLocalError('Напишите, за что расход: через месяц иначе не вспомнить');
      return;
    }
    create.mutate({ date, amount: minor, comment: comment.trim() }, { onSuccess: onClose });
  }

  return (
    <Dialog
      open={open}
      title="Прочий расход"
      description="Трата без сервиса и кошелька: домен, реклама, рублёвый сервис"
      onClose={onClose}
      width={560}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Отмена
          </Button>
          <Button size={size} onClick={submit} loading={create.isPending}>
            Сохранить
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 'var(--space-5)',
          }}
        >
          <DateField label="Дата" value={date} onChange={setDate} />
          <MoneyField label="Сумма, ₽" value={amount} onChange={setAmount} autoFocus />
        </div>
        <Input
          size={size}
          label="За что"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Продление домена kivin.net"
          help="Обязательно, до 200 символов"
        />
        {localError ?? serverError ? (
          <div style={{ font: '400 13px var(--font-text)', color: 'var(--status-danger-fg)' }}>
            {localError ?? serverError}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

export function ExpensesCard() {
  const isMobile = useIsMobile();
  const expenses = useExpenses();
  const remove = useOperation<{ id: string }>('expense.delete');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<ExpenseRow | null>(null);

  const data = expenses.data;

  return (
    <Card padding={isMobile ? 'sm' : 'md'}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-5)',
        }}
      >
        <div>
          <div
            style={{
              font: '500 16px var(--font-display)',
              letterSpacing: 'var(--ls-heading)',
              color: 'var(--text-strong)',
            }}
          >
            Прочие расходы
          </div>
          {data ? (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <Provenance>
                {data.monthCount === 0
                  ? 'В этом месяце прочих расходов нет'
                  : `В этом месяце ${formatMoney(data.monthRubMinor, 'RUB')}, ` +
                    `${data.monthCount} ${plural(data.monthCount, 'запись', 'записи', 'записей')}`}
              </Provenance>
            </div>
          ) : null}
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setAdding(true)}
          iconLeft={<Icon name="Plus" size={16} />}
        >
          Добавить расход
        </Button>
      </div>

      {expenses.isLoading || expenses.isError ? (
        <QueryState query={expenses} what="прочие расходы" />
      ) : !data || data.rows.length === 0 ? (
        <div style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
          Прочих расходов ещё не было. Их можно внести здесь или командой бота
          <span className="kj-mono"> /expense 1500 продление домена</span>.
        </div>
      ) : (
        <div className="kj-scroll-x">
          <table className="kj-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>За что</th>
                <th>Сумма</th>
                <th>Откуда</th>
                <th>Кто</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.id}>
                  <td data-label="Дата" className="kj-mono">
                    {formatDate(row.date)}
                  </td>
                  <td data-label="За что" style={{ color: 'var(--text-strong)' }}>
                    {row.comment}
                  </td>
                  <td data-label="Сумма">
                    <Money minor={row.amount} currency={row.currency} size="sm" />
                    {row.currency !== 'RUB' && row.rubCost !== null ? (
                      <Provenance>{formatMoney(row.rubCost, 'RUB')}</Provenance>
                    ) : null}
                  </td>
                  <td data-label="Откуда" style={{ color: 'var(--text-muted)' }}>
                    {row.walletName ? `${row.walletName}, ` : ''}
                    {recordSourceLabel(row.source)}
                  </td>
                  <td data-label="Кто">{row.author ?? '—'}</td>
                  <td data-label="" style={{ textAlign: 'right' }}>
                    <Button variant="ghost" size="sm" onClick={() => setDeleting(row)}>
                      Удалить
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddExpenseDialog open={adding} onClose={() => setAdding(false)} />

      <Dialog
        open={deleting !== null}
        title="Удалить расход"
        description={
          deleting
            ? `«${deleting.comment}», ${formatMoney(deleting.amount, deleting.currency)}. Расход уйдёт из трат месяца, запись останется в журнале действий.`
            : ''
        }
        onClose={() => setDeleting(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (!deleting) return;
                remove.mutate({ id: deleting.id }, { onSuccess: () => setDeleting(null) });
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </Card>
  );
}
